import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Loader2 } from "lucide-react";
import axios from "axios";
import { Checkbox } from "../ui/checkbox";
import { ContactSuccess } from "./ContactSuccess";
import { trackBetaSignup } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CATEGORIES = [
  { value: "donor", label: "Donor" },
  { value: "student", label: "Student" },
  { value: "partner", label: "Partner (school or org)" },
  { value: "board", label: "Board or investor" },
  { value: "curious", label: "Just curious" },
];

const inputCls =
  "w-full rounded-md border border-border bg-sand px-4 py-3 font-serif text-base text-ink placeholder:text-ink/30 focus:border-ink focus:outline-none focus:ring-2 focus:ring-ink/10";

const Field = ({ label, hint, error, children }) => (
  <label className="block">
    <span className="text-[13px] font-semibold uppercase tracking-[0.22em] text-olive">
      {label}
      {hint && <span className="ml-1.5 normal-case tracking-normal text-olive/60">{hint}</span>}
    </span>
    <div className="mt-2">{children}</div>
    {error && <p className="mt-1.5 text-[13px] text-scarlet" role="alert">{error}</p>}
  </label>
);

/**
 * ContactForm — one form, two jobs. Sends a message to the category inbox
 * and/or adds the sender to the launch waitlist, via `POST /api/contact`.
 */
export const ContactForm = () => {
  const [form, setForm] = useState({ name: "", email: "", category: "", message: "" });
  const [notify, setNotify] = useState(true);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const hasMessage = form.message.trim().length > 0;

  const validate = () => {
    const e = {};
    if (!EMAIL_RE.test(form.email.trim())) e.email = "Enter a valid email.";
    if (!form.category) e.category = "Pick the one that fits best.";
    if (!hasMessage && !notify) e.message = "Add a short message, or tick the box below so we know to write when campaigns go live.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (status === "loading" || !validate()) return;
    setStatus("loading");
    setErrorMsg("");
    try {
      const res = await axios.post(`${API}/contact`, {
        name: form.name.trim() || null,
        email: form.email.trim().toLowerCase(),
        category: form.category,
        message: hasMessage ? form.message.trim() : null,
        notify,
      });
      setResult(res.data);
      setStatus("success");
      if (notify) trackBetaSignup({ role: form.category, source: "contact_page", alreadySignedUp: !!res.data.already_signed_up });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setStatus("error");
      setErrorMsg(
        typeof detail === "string"
          ? detail
          : "Something didn't send. Please try again in a moment, or email hellofrom@myrichaunt.org directly."
      );
    }
  };

  return (
    <div data-testid="contact-form-card" className="rounded-2xl border-2 border-ink/10 bg-bone p-6 shadow-sm lg:p-10">
      <AnimatePresence mode="wait">
        {status !== "success" ? (
          <motion.form
            key="form"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            onSubmit={handleSubmit}
            className="flex flex-col gap-5"
            data-testid="contact-form"
          >
            <Field label="Your name" hint="(optional)">
              <input type="text" value={form.name} onChange={set("name")} placeholder="Jane Doe" data-testid="contact-field-name" className={inputCls} />
            </Field>

            <Field label="Email address" error={errors.email}>
              <input type="email" value={form.email} onChange={set("email")} placeholder="you@somewhere.org" data-testid="contact-field-email" className={inputCls} />
            </Field>

            <fieldset>
              <legend className="text-[13px] font-semibold uppercase tracking-[0.22em] text-olive">I am a…</legend>
              <div className="mt-3 flex flex-wrap gap-2" data-testid="contact-field-category">
                {CATEGORIES.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, category: c.value }))}
                    data-testid={`contact-category-${c.value}`}
                    aria-pressed={form.category === c.value}
                    className={`rounded-full px-4 py-2 text-base font-medium transition-colors ${ form.category === c.value ? "bg-ink text-sand" : "border border-border bg-sand text-ink hover:border-ink" }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
              {errors.category && <p className="mt-1.5 text-[13px] text-scarlet" role="alert">{errors.category}</p>}
            </fieldset>

            <Field label="Message" hint={notify ? "(optional)" : undefined} error={errors.message}>
              <textarea
                value={form.message}
                onChange={set("message")}
                rows={5}
                maxLength={4000}
                placeholder="Tell us a bit about why you're getting in touch."
                data-testid="contact-field-message"
                className={`${inputCls} resize-y font-sans text-base leading-relaxed`}
              />
              <p className="mt-1 text-right text-[13px] text-olive/60">{form.message.length}/4000</p>
            </Field>

            <label className="flex items-start gap-3 rounded-xl border border-ink/10 bg-sand px-4 py-3.5" data-testid="contact-notify-row">
              <Checkbox
                checked={notify}
                onCheckedChange={(v) => setNotify(v === true)}
                data-testid="contact-notify-checkbox"
                className="mt-0.5 h-5 w-5 rounded border-ink/40 data-[state=checked]:bg-ink data-[state=checked]:text-sand"
              />
              <span className="text-[13px] leading-snug text-ink">
                Tell me when verified student campaigns go live.
                <span className="block text-[13px] text-olive">One note on launch day, nothing else.</span>
              </span>
            </label>

            <button
              type="submit"
              disabled={status === "loading"}
              data-testid="contact-submit"
              className="mt-1 inline-flex w-full items-center justify-center gap-2 rounded-full bg-ink px-6 py-4 text-base font-semibold text-sand transition-colors hover:bg-forest disabled:opacity-60 sm:w-auto"
            >
              {status === "loading" ? (
                <><Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />Sending…</>
              ) : (
                <>{hasMessage ? "Send message" : "Notify me when we launch"}<ArrowRight className="h-4 w-4" strokeWidth={2} /></>
              )}
            </button>

            {status === "error" && (
              <p role="alert" data-testid="contact-error" className="rounded-md bg-scarlet/8 px-3 py-2 text-[13px] text-scarlet">{errorMsg}</p>
            )}

            <p className="text-[13px] leading-relaxed text-olive">
              We only use your email to reply, or to tell you when campaigns go live. No lists, no marketing.
            </p>
          </motion.form>
        ) : (
          <ContactSuccess key="success" result={result} />
        )}
      </AnimatePresence>
    </div>
  );
};
